/**
 * ForgotPasswordPage — public route.
 * Submits email → backend sends reset link via Brevo if account exists.
 * Always shows a generic success message (anti-enumeration).
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowLeft, KeyRound, Check, AlertTriangle } from 'lucide-react';
import SEO from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'कुछ गलत हो गया। कृपया पुनः प्रयास करें।');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary cosmic-bg-alt text-text-main flex flex-col">
      <SEO title="पासवर्ड भूल गए · ViratAstro Admin" noIndex />

      <div className="container-custom py-8 flex-1">
        <Link to="/admin/login" className="inline-flex items-center gap-2 text-gold/80 hover:text-gold mb-6 text-sm" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" /> Login Page
        </Link>

        <div className="max-w-md mx-auto">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-3">
              <KeyRound className="w-7 h-7 text-gold" />
            </div>
            <h1 className="font-heading text-2xl">पासवर्ड भूल गए?</h1>
            <p className="text-text-muted text-sm mt-1">अपना registered email दर्ज करें — हम 20 मिनट तक valid एक secure reset link भेजेंगे।</p>
          </div>

          {sent ? (
            <div className="glass-card p-6 text-center" data-testid="forgot-sent-card">
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center mx-auto mb-3">
                <Check className="w-7 h-7 text-emerald-300" />
              </div>
              <h2 className="font-heading text-lg text-emerald-300">Email भेज दिया गया</h2>
              <p className="text-text-muted text-sm mt-3 leading-relaxed">
                यदि यह email हमारे system में registered है, तो आपको कुछ ही मिनटों में reset link मिल जाएगा।<br/>
                कृपया अपना <strong className="text-gold">Inbox</strong> और <strong className="text-gold">Spam folder</strong> दोनों check करें।
              </p>
              <p className="text-[11px] text-text-muted/70 mt-4">⏱ Link 20 मिनट तक valid है · 🔒 केवल एक बार उपयोग योग्य</p>
              <Link to="/admin/login" className="inline-block mt-5 text-gold hover:underline text-sm">← Login पर वापस</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="glass-card p-6 space-y-4" data-testid="forgot-password-form">
              <div>
                <label className="text-xs uppercase tracking-wider text-gold/80 font-semibold">Registered Email</label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted/60" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    data-testid="forgot-email-input"
                    placeholder="admin@example.com"
                    className="w-full bg-black/30 border border-white/15 rounded-lg pl-10 pr-3 py-2.5 text-text-main focus:outline-none focus:border-gold/50"
                  />
                </div>
              </div>

              {error && (
                <div className="bg-rose-500/15 border border-rose-400/40 rounded-lg p-3 text-rose-300 text-sm flex gap-2" data-testid="forgot-error">
                  <AlertTriangle className="w-4 h-4 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email}
                data-testid="submit-forgot"
                className="w-full btn-gold py-3 rounded-lg font-semibold disabled:opacity-50"
              >
                {loading ? 'भेजा जा रहा है...' : 'Reset Link भेजें'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
